import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import axios from "axios";
import { useToast } from "./ToastContext";
import { axiosPublic } from "../utils/axiosConfig";
import { parsePriceText, calculateItemTotalCents } from "../utils/price";
import { toWhatsAppDigitsRegional } from '../utils/phone';

// Platform detection helpers
function isIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';
  return /iPhone|iPad|iPod/i.test(ua) || (platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function isIOSWebView() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // iOS webviews often miss the "Safari" token but have AppleWebKit
  return isIOS() && /AppleWebKit/i.test(ua) && !/Safari/i.test(ua);
}

// SSR safety guard
function isClientSide() {
  return typeof window !== 'undefined' && typeof navigator !== 'undefined';
}

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
const TEMP_CART_KEY = "temp_cart_items";

function getSessionId() {
  let sessionId = localStorage.getItem("sessionId");
  if (!sessionId) {
    sessionId = Math.random().toString(36).substring(2) + Date.now().toString(36);
    localStorage.setItem("sessionId", sessionId);
  }
  return sessionId;
}

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ products: [], totalCents: 0 });
  const [loading, setLoading] = useState(false);
  const { addToast } = useToast();
  const isAdding = useRef(false); // useRef lock to prevent double adds
  const isInitialMount = useRef(true); // Prevent saving on initial load

  // Load temporary cart from localStorage on initial mount
  useEffect(() => {
    const tempCart = localStorage.getItem(TEMP_CART_KEY);
    if (tempCart) {
      try {
        const parsed = JSON.parse(tempCart);
        // Migrate: ensure each product has a stable cartItemId
        if (parsed && Array.isArray(parsed.products)) {
          const genId = () => (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
          const migrated = parsed.products.map((p) => (
            p && (p.cartItemId ? p : { ...p, cartItemId: genId() })
          ));
          setCart({ products: migrated, totalCents: parsed.totalCents || 0 });
          // Save back migrated data silently
          localStorage.setItem(TEMP_CART_KEY, JSON.stringify({ products: migrated, totalCents: parsed.totalCents || 0 }));
        } else {
          setCart({ products: [], totalCents: 0 });
        }
      } catch (error) {
        localStorage.removeItem(TEMP_CART_KEY);
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes (side effect)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    localStorage.setItem(TEMP_CART_KEY, JSON.stringify(cart));
  }, [cart]);

  // Calculate cart total in cents using price metadata and combinations
  const calculateTotalCents = (products) => {
    return products.reduce((sum, item) => {
      const priceMeta = item.priceMeta || parsePriceText(item.priceText);

      // Calculate total quantity from variant quantities or legacy fields
      let totalQuantity = 0;
      if (item.variantQuantities && Object.keys(item.variantQuantities).length > 0) {
        // Sum all individual variant quantities
        totalQuantity = Object.values(item.variantQuantities).reduce((sum, qty) => sum + qty, 0);
      } else {
        // Fallback to legacy calculation
        const totalCombinations = ((item.selectedColors?.length || 0) > 0 ? item.selectedColors.length : 1) *
          ((item.selectedSizes?.length || 0) > 0 ? item.selectedSizes.length : 1);
        totalQuantity = item.quantity * totalCombinations;
      }

      // Ensure minimum quantity of 1 for calculation
      totalQuantity = Math.max(1, totalQuantity);

      let itemTotalCents;
      if (item.isOffer && priceMeta && priceMeta.type === "multi") {
        // For offers: multiply pack price by how many times pack size is exceeded
        const packsNeeded = Math.ceil(totalQuantity / priceMeta.packSize);
        itemTotalCents = packsNeeded * priceMeta.packPriceCents;
      } else {
        // For regular items: unit price × total quantity
        itemTotalCents = calculateItemTotalCents(priceMeta, totalQuantity);
      }

      return sum + itemTotalCents;
    }, 0);
  };

  // Function to ensure product has all required data before adding to cart
  // ALWAYS fetch fresh data to prevent stale prices from being added to cart
  const ensureProductData = async (product) => {
    try {
      console.log('🔄 Fetching fresh product details for ID:', product.id);
      // Add cache-busting timestamp to ensure fresh data
      const timestamp = new Date().getTime();
      const response = await axiosPublic.get(`/products/${product.id}?t=${timestamp}`);
      if (response.data) {
        console.log('✅ Fetched fresh product details:', {
          name: response.data.name,
          price: response.data.price,
          specialPrice: response.data.specialPrice
        });
        return {
          ...product,
          // Use fresh data from server
          code: response.data.code || '',
          name: response.data.name || product.name || 'Product',
          price: response.data.price || product.price || 0,
          specialPrice: response.data.specialPrice || product.specialPrice || '',
          image: product.image || response.data.images?.[0]?.url || '',
          images: response.data.images || product.images || [],
          // Keep selected variants from the product detail page
          selectedColors: product.selectedColors,
          selectedSizes: product.selectedSizes,
          // Include all other fresh data
          ...response.data
        };
      }
    } catch (error) {
      console.error('❌ Failed to fetch fresh product details:', error);
    }
    return product; // Return as-is if fetch fails
  };

  // Add item to temporary cart (state update only)
  const addToCart = async (product, quantity = 1) => {
    if (isAdding.current) return; // Prevent double calls with ref lock
    isAdding.current = true;

    if (!product || !product.id) {
      console.error('❌ Invalid product data:', product);
      addToast("Invalid product data", "error");
      isAdding.current = false;
      return;
    }

    try {
      // Ensure we have all product data before adding to cart
      const completeProduct = await ensureProductData(product);

      // console.log(' Adding to cart - Complete product data:', {
      //   id: completeProduct.id,
      //   name: completeProduct.name,
      //   code: completeProduct.code,
      //   hasCode: !!completeProduct.code
      // });

      const validQty = Math.max(1, parseInt(quantity) || 1);

      // Now update the cart with the complete product data
      setCart((prevCart) => {
        const existingItemIndex = prevCart.products.findIndex(
          (item) => item.productId === completeProduct.id
        );

        let updatedProducts;
        const genId = () => (typeof crypto !== 'undefined' && crypto.randomUUID) ?
          crypto.randomUUID() :
          `${completeProduct.id}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        if (existingItemIndex !== -1) {
          updatedProducts = [...prevCart.products];
          const existingItem = updatedProducts[existingItemIndex];
          updatedProducts[existingItemIndex] = {
            ...existingItem,
            quantity: existingItem.quantity + validQty,
            cartItemId: existingItem.cartItemId || genId(),
          };
        } else {
          let priceText = String(completeProduct.price ?? "");
          const priceMeta = parsePriceText(priceText);
          const cartItemId = genId();

          // For multi-pack offers (like "3 for R100"), automatically set quantity to pack size
          let finalQuantity = validQty;
          if (priceMeta.type === "multi" && priceMeta.packSize > 1) {
            finalQuantity = priceMeta.packSize;
            // Override priceText to show the full pack price instead of divided unit price
            const packPrice = (priceMeta.packPriceCents / 100).toFixed(2);
            priceText = `R${packPrice}`;
          }

          updatedProducts = [
            ...prevCart.products,
            {
              cartItemId,
              productId: completeProduct.id,
              name: completeProduct.name,
              code: completeProduct.code || '', // This will now have the code
              priceText,
              priceMeta,
              quantity: finalQuantity,
              size: completeProduct.size || '',
              color: completeProduct.color || '',
              image: completeProduct.image || '',
              images: completeProduct.images || [],
              sizes: completeProduct.sizes || [],
              selectedColors: completeProduct.selectedColors || [],
              selectedSizes: completeProduct.selectedSizes || [],
              colors: completeProduct.colors || [],
              availableSizes: completeProduct.availableSizes || [],
              shop: completeProduct.shop, // ADD SHOP INFO FOR IOS MESSAGE
              // Store original offer info for WhatsApp messaging
              isOffer: priceMeta.type === "multi" && priceMeta.packSize > 1,
              originalOfferText: priceMeta.type === "multi" ? priceMeta.original : null,
            },
          ];
        }

        const updatedCart = {
          products: updatedProducts,
          totalCents: calculateTotalCents(updatedProducts),
        };

        // console.log('🛒 Updated cart with product:', {
        //   productId: completeProduct.id,
        //   code: completeProduct.code,
        //   cartItemCount: updatedProducts.length
        // });

        return updatedCart;
      });
    } catch (error) {
      console.error('❌ Error adding to cart:', error);
      addToast("Failed to add item to cart", "error");
    } finally {
      isAdding.current = false;
    }

    // This code is now handled in the try-catch block above
  };

  // Update quantity in temporary cart
  const updateQuantity = (productId, newQuantity, color = "", size = "") => {

    setCart((prevCart) => {
      const updatedProducts = prevCart.products.map((item) => {
        if (item.productId === productId && item.color === color && item.size === size) {
          return {
            ...item,
            quantity: Math.max(1, parseInt(newQuantity) || 1),
          };
        }
        return item;
      });

      const updatedCart = {
        products: updatedProducts,
        totalCents: calculateTotalCents(updatedProducts),
      };
      return updatedCart;
    });
  };

  // Update item options (color/size) in temporary cart
  const updateItemOptions = (productId, oldColor, oldSize, newColor, newSize) => {

    setCart((prevCart) => {
      // Find the existing item
      const existingItemIndex = prevCart.products.findIndex(
        (item) => item.productId === productId && item.color === oldColor && item.size === oldSize
      );

      if (existingItemIndex === -1) {
        return prevCart;
      }

      // Get the existing item
      const existingItem = prevCart.products[existingItemIndex];

      // Check if an item with the new options already exists
      const duplicateItemIndex = prevCart.products.findIndex(
        (item) => item.productId === productId && item.color === newColor && item.size === newSize
      );

      let updatedProducts;

      if (duplicateItemIndex !== -1 && duplicateItemIndex !== existingItemIndex) {
        // If an item with the new options exists, merge quantities
        updatedProducts = [...prevCart.products];

        // Update the duplicate item with combined quantity
        updatedProducts[duplicateItemIndex] = {
          ...updatedProducts[duplicateItemIndex],
          quantity: updatedProducts[duplicateItemIndex].quantity + existingItem.quantity
        };

        // Remove the original item
        updatedProducts.splice(existingItemIndex, 1);
      } else {
        // Update the existing item with new options
        updatedProducts = [...prevCart.products];
        updatedProducts[existingItemIndex] = {
          ...existingItem,
          color: newColor,
          size: newSize
        };
      }

      const updatedCart = {
        products: updatedProducts,
        totalCents: calculateTotalCents(updatedProducts),
      };
      return updatedCart;
    });
  };

  // Update selected colors/sizes variants for an item
  const updateSelectedVariants = (productId, color, size, selectedColors, selectedSizes) => {

    setCart((prevCart) => {
      const updatedProducts = prevCart.products.map((item) => {
        if (item.productId === productId && item.color === color && item.size === size) {

          // Initialize quantities for newly selected colors if item has no sizes
          const currentQuantities = item.variantQuantities || {};
          const newQuantities = { ...currentQuantities };
          const hasSizes = item.sizes && item.sizes.length > 0;

          if (!hasSizes && selectedColors && selectedColors.length > 0) {
            selectedColors.forEach(c => {
              const variantKey = `${c}-`; // Empty size key
              // If this variant doesn't have a quantity yet, set it to 1
              if (!newQuantities[variantKey]) {
                newQuantities[variantKey] = 1;
              }
            });

            // Cleanup: remove quantities for colors that were unselected
            Object.keys(newQuantities).forEach(key => {
              const [c, s] = key.split('-');
              // Only remove if it's a color-only variant (empty size) and color is no longer selected
              if (s === '' && !selectedColors.includes(c)) {
                delete newQuantities[key];
              }
            });
          }

          return {
            ...item,
            selectedColors,
            selectedSizes,
            variantQuantities: newQuantities
          };
        }
        return item;
      });

      const updatedCart = {
        products: updatedProducts,
        totalCents: calculateTotalCents(updatedProducts),
      };
      return updatedCart;
    });
  };

  // Update quantity for a specific color/size combination
  const updateVariantQuantity = (productId, color, size, quantity) => {
    setCart((prevCart) => {
      const updatedProducts = prevCart.products.map((item) => {
        if (item.productId === productId) {
          const variantKey = `${color}-${size}`;
          const currentQuantities = item.variantQuantities || {};
          const newQuantities = {
            ...currentQuantities,
            [variantKey]: Math.max(0, parseInt(quantity) || 0)
          };

          // Remove zero quantities
          Object.keys(newQuantities).forEach(key => {
            if (newQuantities[key] === 0) {
              delete newQuantities[key];
            }
          });

          // For offers, preserve the original pack quantity - don't update item.quantity
          // For non-offers, update quantity to sum of variants
          const shouldUpdateQuantity = !item.isOffer;

          return {
            ...item,
            variantQuantities: newQuantities,
            // Update legacy fields for backward compatibility
            selectedColors: Object.keys(newQuantities).map(k => k.split('-')[0]).filter((v, i, a) => a.indexOf(v) === i),
            selectedSizes: Object.keys(newQuantities).map(k => k.split('-')[1]).filter((v, i, a) => v && a.indexOf(v) === i),
            ...(shouldUpdateQuantity && {
              quantity: Object.values(newQuantities).reduce((sum, q) => sum + q, 0) || 1
            })
          };
        }
        return item;
      });

      const updatedCart = {
        products: updatedProducts,
        totalCents: calculateTotalCents(updatedProducts),
      };
      return updatedCart;
    });
  };

  // Get quantity for a specific color/size combination
  const getVariantQuantity = (productId, color, size) => {
    const item = cart.products.find(p => p.productId === productId);
    if (!item || !item.variantQuantities) return 0;
    const variantKey = `${color}-${size}`;
    return item.variantQuantities[variantKey] || 0;
  };

  // Remove item from temporary cart
  const removeItem = (productId, color = "", size = "") => {

    setCart((prevCart) => {
      const updatedProducts = prevCart.products.filter(
        (item) => !(item.productId === productId && item.color === color && item.size === size)
      );

      const updatedCart = {
        products: updatedProducts,
        totalCents: calculateTotalCents(updatedProducts),
      };
      return updatedCart;
    });

    addToast("Item removed from cart", "success");
  };

  // Clear temporary cart
  const clearCart = () => {
    setCart({ products: [], totalCents: 0 });
    localStorage.removeItem(TEMP_CART_KEY);
  };

  // iOS Detection Utility
  const isIOS = () => {
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    const platform = navigator.platform || '';
    if (/iPad|iPhone|iPod/.test(ua)) return true;
    if (platform === 'MacIntel' && navigator.maxTouchPoints > 1) return true; // iPadOS
    return false;
  };



  // Save cart to database (called when WhatsApp button is clicked)
  const saveCartToDatabase = async (runnerId = null, phoneValidation = null, deviceInfo = null, whatsappMessage = null) => {
    if (cart.products.length === 0) {
      addToast("Cart is empty", "error");
      return null;
    }

    setLoading(true);
    const sessionId = getSessionId();

    try {
      // Create cart in database with product links (backend expects 'price' as a number and 'total')
      const products = cart.products.map(item => {
        const priceMeta = item.priceMeta || parsePriceText(item.priceText);
        const unitPriceCents = priceMeta.unitPriceCents || 0;
        // Calculate total combinations for correct pricing
        const totalCombinations = ((item.selectedColors?.length || 0) > 0 ? item.selectedColors.length : 1) *
          ((item.selectedSizes?.length || 0) > 0 ? item.selectedSizes.length : 1);

        return {
          productId: item.productId,
          name: item.name || 'Product', // Fallback name
          code: item.code || '', // Add product code
          price: unitPriceCents / 100, // numeric unit price in Rand (per item)
          quantity: (item.quantity || 1) * totalCombinations, // TOTAL quantity including all combinations
          size: item.size || '',
          color: item.color || '',
          image: item.image || '',
          productLink: `${window.location.origin}/product/${btoa(item.productId.replace(/[+/=]/g, (m) => ({ '+': '-', '/': '_', '=': '' }[m] || m)))}`,
          // Include variant selections for WhatsApp messaging
          selectedColors: item.selectedColors || [],
          selectedSizes: item.selectedSizes || [],
          totalCombinations,
          unitPriceCents,
        };
      });

      const totalNumber = (cart.totalCents ?? calculateTotalCents(cart.products)) / 100;

      const cartData = {
        sessionId,
        products,
        total: totalNumber,
        // Include runner ID for tracking (passed as parameter)
        runnerId,
        // Add phone and device info for backend logging
        phoneValidation,
        deviceInfo,
        whatsappMessage, // Include the generated WhatsApp message
      };

      const response = await axios.post(`${API_URL}/api/carts/save-whatsapp`, cartData);
      addToast("Cart saved successfully!", "success");

      return response.data; // Return saved cart with ID for WhatsApp message

    } catch (error) {
      addToast("Failed to save cart", "error");
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Generate WhatsApp message with cart details and product links
  const generateWhatsAppMessage = async (savedCart) => {
    if (!savedCart || !savedCart.products) {
      return "";
    }

    // FETCH PRODUCT CODES FROM DATABASE
    const productsWithCodes = [];
    for (const item of savedCart.products) {
      try {
        const response = await axiosPublic.get(`/products/${item.productId}?populate=shop`);
        const productFromDB = response.data;

        // Get final code from sources
        const dbCode = productFromDB.code;
        const savedCode = item.code;
        const localCode = cart.products.find(p => p.productId === item.productId)?.code;
        const finalCode = dbCode || savedCode || localCode || '';

        productsWithCodes.push({
          ...item,
          code: finalCode, // USE FOUND CODE
          name: productFromDB.name || item.name, // Also get fresh name
          shop: productFromDB.shop, // Add shop information
        });
      } catch (error) {
        // Fallback to saved cart data and local cart
        const localCode = cart.products.find(p => p.productId === item.productId)?.code;
        const finalCode = item.code || localCode || '';
        productsWithCodes.push({ ...item, code: finalCode });
      }
    }

    // Determine order title based on shop affiliation
    const uniqueShops = [...new Set(productsWithCodes.map(item => item.shop?.name).filter(Boolean))];
    let orderTitle = "*quick runner Order*";

    if (uniqueShops.length === 1 && uniqueShops[0]) {
      // All products from same shop
      orderTitle = `*${uniqueShops[0]} Order*`;
    } else if (uniqueShops.length > 1) {
      // Products from multiple shops
      orderTitle = "*Multi-Shop Order*";
    }

    let message = `${orderTitle}\n\n`;

    // Process each product individually with its variant selections
    let index = 1;
    let totalGrand = 0;

    productsWithCodes.forEach((item) => {
      // console.log('Processing item:', item);

      // ✅ GET PRODUCT NAME FROM LOCAL CART DATA (not saved cart)
      // Use productId only to find the cart item since multi-variant items use arrays
      const localCart = cart.products.find(p => p.productId === item.productId);

      // Use correct product name and code from local cart or fallback to saved
      const productName = localCart?.name || item.name || 'Product';
      const productCode = localCart?.code || item.code || '';

      // Use original pricing text from local cart to preserve bulk pricing like "4 for R100"
      const originalPriceText = localCart?.priceText;
      const isOffer = localCart?.isOffer;
      const originalOfferText = localCart?.originalOfferText;

      // Get variant quantities from local cart
      const variantQuantities = localCart?.variantQuantities || {};

      // If we have individual variant quantities, group by color
      if (Object.keys(variantQuantities).length > 0) {
        // Group variants by color
        const variantsByColor = {};
        Object.entries(variantQuantities).forEach(([variantKey, quantity]) => {
          if (quantity > 0) {
            const [color, size] = variantKey.split('-');
            if (!variantsByColor[color]) {
              variantsByColor[color] = [];
            }
            variantsByColor[color].push({ size, quantity });
          }
        });

        // Display each color with its sizes
        Object.entries(variantsByColor).forEach(([color, sizes]) => {
          // Calculate total quantity for this color
          const totalQuantityForColor = sizes.reduce((sum, s) => sum + s.quantity, 0);

          // Calculate total price for this color group using offer pricing logic
          let totalPriceForColor = 0;
          const itemPriceMeta = item.priceMeta;
          if (isOffer && itemPriceMeta && itemPriceMeta.type === "multi") {
            // For offers: multiply pack price by how many times pack size is exceeded
            const packsNeeded = Math.ceil(totalQuantityForColor / itemPriceMeta.packSize);
            totalPriceForColor = packsNeeded * itemPriceMeta.packPriceCents;
          } else {
            // For regular items: unit price × total quantity
            totalPriceForColor = (itemPriceMeta?.unitPriceCents || 0) * totalQuantityForColor;
          }

          // Get price display text - use offer text for offers, otherwise original text
          const priceDisplay = isOffer && originalOfferText ? originalOfferText :
            originalPriceText || item.priceText || `R${(parseFloat(item.price) || 0).toFixed(2)}`;

          // Build message - EXACTLY MATCHING ANDROID FORMAT
          message += `${index}. *${productName}*\n`;

          // Add shop name if exists - FOR IOS COMPATIBILITY
          if (item.shop?.name) {
            message += `   Shop: ${item.shop.name}\n`;
          }

          // Add code if exists - EXACTLY MATCHING ANDROID
          if (productCode && productCode.trim()) {
            message += `   Code: ${productCode}\n`;
          }

          // Color and sizes for this variant group
          message += `   Color: ${color}\n`;

          // Only show sizes if they exist (not empty string)
          const validSizes = sizes.filter(s => s.size && s.size.trim() !== '');
          if (validSizes.length > 0) {
            const sizeList = validSizes.map(s => `${s.size} (${s.quantity})`).join(', ');
            message += `   Sizes: ${sizeList}\n`;
          }

          // Total quantity for this color
          message += `   Total Quantity: ${totalQuantityForColor}\n`;
          message += `   Unit Price: ${priceDisplay}\n`;
          message += `   Total: R${(totalPriceForColor / 100).toFixed(2)}\n\n`;

          index++;
        });
      } else {
        // Fallback to old logic if no variant quantities
        // ✅ CALCULATE PRICING CORRECTLY WITH COMBINATIONS
        const unitPrice = parseFloat(item.price) || 0; // Direct Rand value
        const totalCombinations = item.totalCombinations || 1;
        // For multi-select variants, ignore base quantity - user quantity is 1 per combination
        const totalQuantity = totalCombinations > 1 ? totalCombinations : (item.quantity || 1);

        const itemTotal = unitPrice * totalQuantity; // CORRECT: price × total quantity
        totalGrand += itemTotal;

        // Get price display text - use offer text for offers, otherwise original text
        const priceDisplay = isOffer && originalOfferText ? originalOfferText :
          originalPriceText || item.priceText || `R${unitPrice.toFixed(2)}`;

        // Product header
        message += `${index}. *${productName}*\n`;

        // Shop name - INCLUDE IF EXISTS
        if (item.shop?.name) {
          message += `   Shop: ${item.shop.name}\n`;
        }

        // Product code - GUARANTEED TO INCLUDE IF EXISTS
        if (productCode && productCode.trim()) {
          message += `   Code: ${productCode}\n`;
        }

        // VARIANT SELECTIONS - GROUP SIZES BY COLOR
        if (item.selectedColors && Array.isArray(item.selectedColors) && item.selectedColors.length > 0 &&
          item.selectedSizes && Array.isArray(item.selectedSizes) && item.selectedSizes.length > 0) {
          // User selected both colors and sizes - show grouped format
          const colorSizeGroups = item.selectedColors.map(color => {
            return `${color} (${item.selectedSizes.join(', ')})`;
          }).join(', ');
          message += `   Selections: ${colorSizeGroups}\n`;
        } else if (item.selectedColors && Array.isArray(item.selectedColors) && item.selectedColors.length > 0) {
          // Only colors selected
          if (item.selectedColors.length > 1) {
            message += `   Colors Selected: ${item.selectedColors.join(', ')}\n`;
          } else {
            message += `   Color Selected: ${item.selectedColors[0]}\n`;
          }
        } else if (item.color) {
          message += `   Color: ${item.color}\n`;
        }

        // Only show sizes separately if no colors were selected
        if (!(item.selectedColors && Array.isArray(item.selectedColors) && item.selectedColors.length > 0)) {
          if (item.selectedSizes && Array.isArray(item.selectedSizes) && item.selectedSizes.length > 0) {
            if (item.selectedSizes.length > 1) {
              message += `   Sizes Selected: ${item.selectedSizes.join(', ')}\n`;
            } else {
              message += `   Size Selected: ${item.selectedSizes[0]}\n`;
            }
          } else if (item.size) {
            message += `   Size: ${item.size}\n`;
          }
        }

        // COMBINATIONS & QUANTITY - CORRECTED CALCULATION
        if (totalCombinations > 1) {
          message += `   Variant Combinations: ${totalCombinations}\n`;
        }
        message += `   Quantity: ${totalQuantity}\n`;
        message += `   Unit Price: ${priceDisplay}\n`;

        // Product link
        const productLink = item.productLink;
        if (productLink) {
          message += `   View Product: ${productLink}\n`;
        }
        message += `\n`;

        index++;
      }
    });

    // GRAND TOTAL - GUARANTEED CORRECT
    const cartTotal = parseFloat(savedCart.total) || totalGrand;
    message += `*TOTAL: R${cartTotal.toFixed(2)}*\n\n`;

    // Cart metadata
    if (savedCart._id) {
      message += `Cart ID: ${savedCart._id}\n`;
    }
    message += `Order Time: ${new Date().toLocaleString()}\n\n`;
    message += "Please confirm this order and let me know about delivery details. Thank you!";

    // console.log('📱 WhatsApp Message (non-iOS):', message); // SHOW FULL MESSAGE TO USER FOR TESTING
    return encodeURIComponent(message);
  };

  // Build a lightweight WhatsApp message synchronously (no awaits)
  function buildLocalWaMessage(cart) {
    try {
      let message = '*Order*\n\n';
      let totalGrand = 0;
      let index = 1;

      cart.products.forEach((item) => {
        // For iOS, we need to work with what we have
        const productName = item.name || 'Product';

        // Try to get code from any available source
        const productCode = item.code ||
          (item.product && item.product.code) ||
          '';

        // Use original pricing text from local cart to preserve bulk pricing like "4 for R100"
        const originalPriceText = item.priceText;
        const isOffer = item.isOffer;
        const originalOfferText = item.originalOfferText;

        // Get variant quantities from local cart
        const variantQuantities = item.variantQuantities || {};

        // If we have individual variant quantities, group by color
        if (Object.keys(variantQuantities).length > 0) {
          // Create individual line items for each color/size combination
          Object.entries(variantQuantities).forEach(([variantKey, quantity]) => {
            if (quantity > 0) {
              const [color, size] = variantKey.split('-');

              // Extract numeric price from price text (e.g. "R25.00" -> 25)
              const unitPrice = item.priceText ? parseFloat(item.priceText.replace(/R/gi, '').replace(/,/g, '')) || 0 :
                parseFloat(item.price) || 0;
              const itemTotal = unitPrice * quantity;
              totalGrand += itemTotal;

              // Get price display text - use offer text for offers, otherwise original text
              const priceDisplay = isOffer && originalOfferText ? originalOfferText :
                originalPriceText || item.priceText || `R${unitPrice.toFixed(2)}`;

              // Build message - EXACTLY MATCHING ANDROID FORMAT
              message += `${index}. *${productName}*\n`;

              // Add shop name if exists - FOR IOS COMPATIBILITY
              if (item.shop?.name) {
                message += `   Shop: ${item.shop.name}\n`;
              }

              // Add code if exists - EXACTLY MATCHING ANDROID
              if (productCode && productCode.trim()) {
                message += `   Code: ${productCode}\n`;
              }

              // Color and Size for this specific variant
              message += `   Color: ${color}\n`;
              if (size && size.trim() !== '') {
                message += `   Size: ${size}\n`;
              }

              // Quantity for this specific variant
              message += `   Quantity: ${quantity}\n`;
              message += `   Unit Price: ${priceDisplay}\n`;
              message += `   Total: R${(itemTotal).toFixed(2)}\n\n`;

              index++;
            }
          });
        } else {
          // Calculate quantities and price - MATCHING ANDROID LOGIC
          const totalCombinations = ((item.selectedColors?.length || 0) > 0 ? item.selectedColors.length : 1) *
            ((item.selectedSizes?.length || 0) > 0 ? item.selectedSizes.length : 1);
          // For multi-select variants, ignore base quantity - user quantity is 1 per combination
          const totalQuantity = totalCombinations > 1 ? totalCombinations : (item.quantity || 1);
          // Extract numeric price from price text (e.g. "R25.00" -> 25)
          const unitPrice = item.priceText ? parseFloat(item.priceText.replace(/R/gi, '').replace(/,/g, '')) || 0 :
            parseFloat(item.price) || 0;
          const itemTotal = unitPrice * totalQuantity;
          totalGrand += itemTotal;

          // Get price display text - use offer text for offers, otherwise original text
          const priceDisplay = isOffer && originalOfferText ? originalOfferText :
            originalPriceText || item.priceText || `R${unitPrice.toFixed(2)}`;

          // Build message - EXACTLY MATCHING ANDROID FORMAT
          message += `${index}. *${productName}*\n`;

          // Add shop name if exists - FOR IOS COMPATIBILITY
          if (item.shop?.name) {
            message += `   Shop: ${item.shop.name}\n`;
          }

          // Add code if exists - EXACTLY MATCHING ANDROID
          if (productCode && productCode.trim()) {
            message += `   Code: ${productCode}\n`;
          }

          // Handle colors and sizes - GROUP SIZES BY COLOR
          if (item.selectedColors?.length > 0 && item.selectedSizes?.length > 0) {
            // User selected both colors and sizes - show grouped format
            const colorSizeGroups = item.selectedColors.map(color => {
              return `${color} (${item.selectedSizes.join(', ')})`;
            }).join(', ');
            message += `   Selections: ${colorSizeGroups}\n`;
          } else if (item.selectedColors?.length > 0) {
            // Only colors selected
            if (item.selectedColors.length > 1) {
              message += `   Colors Selected: ${item.selectedColors.join(', ')}\n`;
            } else {
              message += `   Color Selected: ${item.selectedColors[0]}\n`;
            }
          } else if (item.color) {
            message += `   Color: ${item.color}\n`;
          }

          // Only show sizes separately if no colors were selected
          if (!(item.selectedColors?.length > 0)) {
            if (item.selectedSizes?.length > 0) {
              if (item.selectedSizes.length > 1) {
                message += `   Sizes Selected: ${item.selectedSizes.join(', ')}\n`;
              } else {
                message += `   Size Selected: ${item.selectedSizes[0]}\n`;
              }
            } else if (item.size) {
              message += `   Size: ${item.size}\n`;
            }
          }

          // COMBINATIONS & QUANTITY - CORRECTED CALCULATION
          if (totalCombinations > 1) {
            message += `   Variant Combinations: ${totalCombinations}\n`;
          }
          message += `   Quantity: ${totalQuantity}\n`;
          message += `   Unit Price: ${priceDisplay}\n`;
          message += `   Total: R${(itemTotal).toFixed(2)}\n\n`;

          index++;
        }
      });

      // Add total - EXACTLY MATCHING ANDROID
      const cartTotal = cart.totalCents ? (cart.totalCents / 100) : totalGrand;
      message += `*TOTAL: R${cartTotal.toFixed(2)}*`;

      // console.log('📱 iOS WhatsApp Message:', message); // SHOW FULL MESSAGE TO USER
      return encodeURIComponent(message);
    } catch (error) {
      console.error('Error building WhatsApp message:', error);
      return encodeURIComponent('Order details attached.');
    }
  }

  // Synchronous iOS WhatsApp redirect with quick fallback
  // Note: This works in both Safari and in-app WebViews, but may show a popup in WebViews
  function openWhatsAppIOS(runnerDigits, messageEncoded) {
    if (!isClientSide()) return; // SSR guard
    const scheme = `whatsapp://send?phone=${runnerDigits}&text=${messageEncoded}`;
    const web = `https://wa.me/${runnerDigits}?text=${messageEncoded}`;

    let fallbackTimer;
    const onVis = () => {
      document.removeEventListener('visibilitychange', onVis);
      clearTimeout(fallbackTimer);
    };
    document.addEventListener('visibilitychange', onVis, { once: true });

    // MUST be synchronous to preserve the user gesture
    window.location.assign(scheme);

    // Fast fallback if scheme isn't handled
    fallbackTimer = setTimeout(() => {
      if (!document.hidden) window.location.assign(web);
    }, 900);
  }

  // iOS-only: immediate redirect, then background save
  function sendToRunnerIOS(cart, phoneNumber, runnerId, addToast, saveCartToDatabase, clearCart) {
    // console.log('🚀 STARTING iOS WhatsApp validation for:', phoneNumber);
    // console.log('📊 Cart has', cart.products.length, 'products');

    // Use the simplified validation that works for all international numbers
    // console.log('🔍 Validating phone number:', phoneNumber);
    const phoneDigits = toWhatsAppDigitsRegional(phoneNumber);

    if (!phoneDigits) {
      // console.log('❌ Phone number validation failed for:', phoneNumber);
      addToast('Please enter a valid phone number (e.g., +1234567890 or 1234567890)', 'error');
      return;
    }

    // console.log('✅ Phone number validated successfully. Cleaned digits:', phoneDigits);

    // console.log('🎯 PHONE VALIDATION SUCCESS! Final phone digits:', phoneDigits);

    // 1) IMMEDIATELY redirect (NO awaits before this!)
    const msg = buildLocalWaMessage(cart);
    openWhatsAppIOS(phoneDigits, msg);

    // 2) Background: save + (on success) clear cart
    setTimeout(async () => {
      try {
        const phoneValidation = {
          rawInput: phoneNumber,
          isValid: true,
          validatedDigits: phoneDigits,
          formatUsed: phoneDigits ? 'international' : 'clean_digits'
        };
        const deviceInfo = {
          isIOS: true,
          isIOSWebView: isIOSWebView(),
          userAgent: navigator.userAgent,
          platform: navigator.platform || ''
        };

        const savedCart = await saveCartToDatabase(runnerId, phoneValidation, deviceInfo, decodeURIComponent(msg)); // Pass decoded message
        if (!savedCart) return;          // do not clear on failure
        clearCart();
        addToast('Redirected to WhatsApp! Cart saved.', 'success');
      } catch {
        addToast('WhatsApp opened, but saving the cart failed.', 'error');
      }
    }, 0);
  }

  // Handle WhatsApp submission (iOS-aware; ONLY iOS changed)
  const submitToWhatsApp = async (phoneNumber, runnerId) => {
    if (isIOS()) {
      // iOS: synchronous redirect (no awaits before this call)
      sendToRunnerIOS(cart, phoneNumber, runnerId, addToast, saveCartToDatabase, clearCart);
      return;
    }

    // Use the same validation as iOS for consistency
    const phoneDigits = toWhatsAppDigitsRegional(phoneNumber);
    if (!phoneDigits) {
      addToast('Please enter a valid phone number (e.g., +1234567890 or 1234567890)', 'error');
      return;
    }

    const deviceInfo = {
      isIOS: false,
      isIOSWebView: false,
      userAgent: navigator.userAgent,
      platform: navigator.platform
    };

    const phoneValidation = {
      rawInput: phoneNumber,
      isValid: true,
      validatedDigits: phoneDigits
    };

    const savedCart = await saveCartToDatabase(runnerId, phoneValidation, deviceInfo);
    if (!savedCart) return;

    const messageEncoded = await generateWhatsAppMessage(savedCart); // Generate message first
    await axios.put(`${API_URL}/api/carts/${savedCart._id}/whatsapp-message`, { whatsappMessage: decodeURIComponent(messageEncoded) }); // Update with message
    const whatsappUrl = `https://wa.me/${phoneDigits}?text=${messageEncoded}`;

    // TEMPORARILY SHOW iOS MESSAGE FORMAT FOR TESTING (even on non-iOS devices)
    // console.log('🔧 TESTING: What iOS message would look like:', decodeURIComponent(buildLocalWaMessage(cart)));

    window.open(whatsappUrl, '_blank');

    clearCart();
    addToast("Redirected to WhatsApp! Cart has been saved.", "success");
  };

  const value = {
    cart,
    loading,
    addToCart,
    updateQuantity,
    updateItemOptions,
    updateSelectedVariants,
    updateVariantQuantity,
    getVariantQuantity,
    removeItem,
    clearCart,
    saveCartToDatabase,
    submitToWhatsApp,
    generateWhatsAppMessage,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
